import { Link } from "react-router-dom";
import { Sparkles, Leaf, ShieldCheck, HandHeart, Flag, MessageSquareDashed } from "lucide-react";

export default function HowItWorks() {
  return (
    <main className="max-w-4xl mx-auto px-6 pt-14 pb-24" data-testid="how-it-works">
      <p className="accent-label text-[#B79256]">how willoah works</p>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2A2823] leading-[1.05] mt-3">
        The whole trust system, on one page.
      </h1>
      <p className="text-lg text-[#2A2823]/70 mt-6 leading-relaxed max-w-2xl">
        Willoah is where real people share what actually works — recipes, gift ideas, and
        recommendations you can trust, without an algorithm deciding what you see.
        Here's exactly how it stays that way.
      </p>

      <section className="mt-14 space-y-10" data-testid="how-it-works-list">
        <Row
          icon={<Leaf className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />}
          title="Every seed carries a proof-of-use label"
          body={
            <>
              Before anyone plants an idea, they pick a plain-English label for how they actually used it —
              <b> I own this · I made this · I visited this · Used them once · I used to do this</b>.
              You never wonder if the person actually tried the thing.
            </>
          }
        />
        <Row
          icon={<HandHeart className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />}
          title="Field Notes, not stars"
          body={
            <>
              Ratings collapse everything into a single number. Field Notes ask the questions you'd ask a
              friend: <i>how long you've had it, what you paid, whether you'd do it again, the downside, and who it's not for</i>.
              Nothing is optional. Nothing is anonymous.
            </>
          }
        />
        <Row
          icon={<ShieldCheck className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />}
          title="Sponsored content is always clearly labeled"
          body={
            <>
              The Grove is chronological — newest first. Nothing is quietly bumped up. If a seed is
              ever paid to appear, it will carry a visible sponsored label so you always know what
              you're looking at. Brands can pay a flat fee to enter the <Link to="/try-and-critique" className="text-[#4E5A3D] underline underline-offset-2 hover:text-[#3E4830]">Try &amp; Critique pool</Link>,
              but the fee only buys a fair shot at being tested by real users. It does not buy a positive review,
              and it never buys the review's removal.
            </>
          }
        />
        <Row
          icon={<MessageSquareDashed className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />}
          title="Reviews can grow, but they can't be rewritten"
          body={
            <>
              Once a Field Note is posted, the sharer can add dated updates below it ("6 months in — still holds up")
              but they can't edit the original review away. Time-stamped receipts protect the honest voice from second-guessing.
            </>
          }
        />
        <Row
          icon={<Flag className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />}
          title="Trusted is guarded, not gamed"
          body={
            <>
              The Trusted category is for local people, places, and services a real person would
              personally recommend — a dentist, a restaurant, a daycare, a tutor, a contractor.
              If someone reports a Trusted seed, it auto-pauses until the site owner reviews it.
              A merely-negative review will never be removed on a provider's behalf.
            </>
          }
        />
        <Row
          icon={<Sparkles className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />}
          title="No tracking, no dark patterns"
          body={
            <>
              When you share a Willoah link, it's just a link — no hidden tracking, no spying on where
              you clicked from. Print a recipe and it prints clean, no watermarks. And once you leave,
              we leave you alone — no popups begging you to come back. Willoah stays small on purpose.
              A quiet corner, not another feed trying to hook you.
            </>
          }
        />
      </section>

      <div className="mt-16 text-center">
        <Link
          to="/grove"
          data-testid="how-it-works-cta"
          className="inline-flex items-center justify-center rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-7 h-12 text-base"
        >
          Enter The Grove
        </Link>
      </div>
    </main>
  );
}

function Row({ icon, title, body }) {
  return (
    <div className="grid md:grid-cols-[auto,1fr] gap-4 md:gap-6 items-start">
      <div className="w-9 h-9 rounded-full border border-[#B79256]/40 bg-white/70 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h2 className="font-serif-display text-2xl text-[#2A2823]">{title}</h2>
        <p className="text-[#2A2823]/75 mt-2 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
